/**
 * @file: UnreadBadge.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Small red counter chip used to overlay icons (drawer trigger, drawer
 * Notifications item, etc.). Renders nothing when `count` is 0.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

type Props = {
  count: number;
  /** Cap displayed number; anything beyond shows `${max}+`. Default 99. */
  max?: number;
  /** Anchor variant — `overlay` floats over a parent icon, `inline` flows. */
  variant?: 'overlay' | 'inline';
  /** Show a small red dot (no number) when count > 0. */
  dotOnly?: boolean;
  style?: StyleProp<ViewStyle>;
};

export const UnreadBadge: FC<Props> = memo(
  ({ count, max = 99, variant = 'inline', dotOnly = false, style }) => {
    const { rpx } = useResponsiveContext();
    if (!count || count <= 0) return null;

    if (dotOnly) {
      return (
        <View
          style={style}
          css={`
            ${variant === 'overlay'
              ? `position: absolute; top: -${rpx(2)}px; right: -${rpx(2)}px;`
              : ''}
            width: ${rpx(8)}px;
            height: ${rpx(8)}px;
            border-radius: ${rpx(4)}px;
            background-color: #ff3b30;
          `}
        />
      );
    }

    const text = count > max ? `${max}+` : `${count}`;
    const longish = text.length > 1;
    const overlay = variant === 'overlay';
    // For overlay badges anchor the badge's bottom-left corner near the
    // host icon's top-right corner so it visibly hangs off the icon.
    const overlayHeight = rpx(longish ? 16 : 14);

    return (
      <View
        style={style}
        css={`
          ${overlay
            ? `position: absolute; top: -${Math.round(overlayHeight * 0.6)}px;
               right: -${rpx(longish ? 14 : 8)}px;`
            : ''}
          flex-shrink: 0;
          min-width: ${overlay
            ? rpx(longish ? 30 : 14)
            : rpx(longish ? 24 : 16)}px;
          height: ${overlay ? overlayHeight : rpx(16)}px;
          padding: 0 ${rpx(overlay ? 5 : 4)}px;
          border-radius: ${(overlay ? overlayHeight : rpx(16)) / 2}px;
          background-color: #ff3b30;
          align-items: center;
          justify-content: center;
          ${overlay ? `border-width: ${rpx(1.5)}px; border-color: #fff;` : ''}
        `}
      >
        {/* No numberOfLines — the count is at most `${max}+` (3 chars) and the
            badge sizes to it; clamping to one line truncates "99+" → "9…" on
            native when the measured width falls a hair short. */}
        <Typography.Text
          size={rpx(overlay ? 9 : 10)}
          lineHeight={rpx(overlay ? 11 : 12)}
          color="#fff"
          style={{
            fontFamily: FontFamily.NotoSans.Bold,
            textAlign: 'center',
            includeFontPadding: false,
          }}
        >
          {text}
        </Typography.Text>
      </View>
    );
  },
);
